/**
 * HorizontalCardInfo component.
 */
import Addon from '@shared/classes/addon';
import Button from '@components/elements/buttons/ui/cardButton';
import Container from '@components/atoms/container/horizontalCardInfoContainer';
import Content from '@shared/types/enums/content';
import EpgEvent from '@shared/classes/epgEvent';
import Event from '@shared/classes/event';
import FlavourConfig from '@shared/config/flavours';
import Info from '@components/atoms/text/cardInfoText';
import Margin from '@components/atoms/container/cardBottomMarginContainer';
import MediaProduction from '@shared/classes/mediaProduction';
import Plan from '@shared/classes/plan';
import ProgressBar from '@components/elements/progressBar/eventProgressBar';
import Setting from '@shared/classes/setting';
import Title from '@components/atoms/text/horizontalCardTitle';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { CardButtonIcon } from '@shared/types/enums/card';
import { useMemo } from 'react';

interface ButtonData {
  color?: string;
  display?: boolean;
  icon?: CardButtonIcon;
  text?: string;
}

interface Props {
  focused: boolean;
  item: Addon | Event | Setting;
  plan: Plan | null;
  showMask?: boolean;
  showTitle?: boolean;
  progressBar?: {
    color: string;
    percentage: number;
  };
}

export default function HorizontalCardInfo({
  focused = false,
  item,
  plan,
  showMask,
  showTitle,
  progressBar,
}: Props) {
  const episodeNumber = (item as EpgEvent)?.getEpisodeNumber();
  const seasonNumber = (item as EpgEvent)?.getSeasonNumber();

  const { button, info, title } = useMemo(() => {
    let title = item?.title;
    let button: ButtonData = {};
    let hasButton = false;
    let icon;
    let info = '';
    let text = '';

    // Get card info

    switch (item.type) {
      case Content.EPG_EVENT:
        hasButton = true;
        info = `${seasonNumber}${
          episodeNumber && seasonNumber ? ' ' : ''
        }${episodeNumber}`;
        break;
      case Content.MEDIA_PRODUCTION:
        if ((item as MediaProduction)?.streams) {
          hasButton = true;
        }
        info = `${seasonNumber}${
          episodeNumber && seasonNumber ? ' ' : ''
        }${episodeNumber}`;
        break;
      case Content.SEASON:
        hasButton = !!plan;
        info = seasonNumber;
        break;
      case Content.SERIES:
        hasButton = !!plan;
        title = '';
        break;
      case Content.SETTING:
        title = TranslationManager.get(item?.title);
        break;
      case Content.SHOW:
        if ((item as MediaProduction)?.streams) {
          hasButton = true;
        }
        info = episodeNumber;
        break;
    }

    // Get card button

    if (hasButton) {
      const color = plan?.colorHex;
      const display = !!plan;

      if (plan) {
        icon = CardButtonIcon.PLAN;
        text = focused
          ? plan?.name
          : plan?.name?.replace(FlavourConfig.appTitle, '');
      }
      button = {
        color,
        display,
        icon,
        text,
      };
    }

    return { button, info, title };
  }, [focused, item, plan]);

  return (
    <Container showMask={showMask}>
      <div>
        {button?.display ? (
          <Button
            color={button?.color}
            focused={focused}
            iconType={button?.icon}
            text={button?.text}
          />
        ) : null}
      </div>
      <div>
        {info && !plan ? <Info>{info}</Info> : null}
        {showTitle ? <Title>{title}</Title> : null}
        {progressBar ? (
          <ProgressBar
            percentage={progressBar.percentage}
            color={progressBar?.color}
          />
        ) : (
          <Margin />
        )}
      </div>
    </Container>
  );
}
